/**
 * Rating — five Material Symbols stars, read-only or interactive, with an optional
 * localised review count.
 *
 * Ported 1:1 from `tw-components.jsx:403`. Deviations are mechanical only: `cx` and
 * `Icon` are imported, and `React` is imported because this one does use a hook.
 *
 * **Currently dormant.** `ProductCard` renders it only when `rating !== undefined`,
 * and no caller passes `rating` yet: the listing query returns
 * `custitem_customer_review_rating` but nothing in the app has ever displayed it, and
 * there is no review-count field in the schema at all — stars without a count imply
 * reviews the site cannot show. It's vendored anyway so `ProductCard` stays a
 * faithful copy of upstream instead of a fork with a branch cut out. Wire it up when
 * a reviews feature lands.
 */
import React from "react";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";

export function Rating({
  value = 0,
  onChange,
  max = 5,
  size = 22,
  readOnly = false,
  count,
  style = {},
  className = "",
}) {
  const [hover, setHover] = React.useState(0);
  const active = hover || value;
  const px = typeof size === "number" ? size : parseFloat(size) || 22;
  return (
    <span
      className={cx("dnd-rating inline-flex items-center gap-[2px]", className)}
      style={style}
      role="img"
      aria-label={`${value} out of ${max} stars`}
    >
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          tabIndex={readOnly ? -1 : 0}
          disabled={readOnly}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange && onChange(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          style={readOnly ? { cursor: "default" } : undefined}
          className={cx(
            "dnd-rating__star cursor-pointer bg-transparent border-0 p-0 inline-flex [transition:transform_var(--duration-base)_var(--ease-spring),color_var(--duration-fast)]",
            n <= active ? "text-orange-500" : "text-neutral-300",
            !readOnly && "hover:scale-[1.2]"
          )}
        >
          <Icon name="star" size={px} weight={400} fill={n <= active} />
        </button>
      ))}
      {count !== undefined && (
        <span className="ml-[var(--space-2)] font-sans text-sm text-ink-2">
          ({Number(count).toLocaleString()})
        </span>
      )}
    </span>
  );
}
